import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CarouselButtons from '../presentational/CarouselButtons';
import { defrostCard, freezeCard } from 'lib/actions';
import { useRestrictedAction } from 'hooks';
import { CardStatus, ICard } from 'lib/type';
import useCountdownTimer from 'lib/hooks/useCountdownTimer';

interface CarouselButtonsContainerProps {
    onPrevClick: () => void;
    onNextClick: () => void;
    onViewPinClick: () => void;
    start: boolean;
    end: boolean;
    card: ICard | null;
    isDormant: boolean;
}

const CarouselButtonsContainer: FC<CarouselButtonsContainerProps> = ({
    onViewPinClick,
    card,
    isDormant,
}) => {
    const dispatch = useDispatch();
    const status = card?.status;
    const isFrozen = status === CardStatus.TERMINATED;

    const storedEndTime = sessionStorage.getItem('countdownEndTime');

    const initialValue = storedEndTime
        ? Math.max(
              0,
              Math.floor(
                  (Number.parseInt(storedEndTime, 10) - Date.now()) / 1000,
              ),
          )
        : 30;

    const {
        remainingSeconds,
        startCountdown,
        isCountdownActive,
    } = useCountdownTimer(initialValue);

    const isDisabledCard =
        isDormant ||
        !status ||
        ([
            CardStatus.LOST,
            CardStatus.STOLEN,
            CardStatus.DAMAGED,
            CardStatus.ISSUED,
            CardStatus.RISK,
            'N',
        ] as (CardStatus | string)[]).includes(status);

    const { handleRestrictedAction } = useRestrictedAction();

    const toggleFreeze = async () => {
        if (!card) return;

        if (isFrozen) {
            await dispatch(defrostCard(card.cardSerial) as any);
        } else {
            await dispatch(freezeCard(card.cardSerial) as any);
        }

        const endTime = Date.now() + 30 * 1000;
        sessionStorage.setItem('countdownEndTime', endTime.toString());
        startCountdown();
    };

    useEffect(() => {
        if (storedEndTime) {
            const secondsLeft = Math.max(
                0,
                Math.floor(
                    (Number.parseInt(storedEndTime, 10) - Date.now()) / 1000,
                ),
            );
            if (secondsLeft > 0) {
                startCountdown();
            }
        }
    }, [storedEndTime, startCountdown]);

    return (
        <CarouselButtons
            onViewPinClick={onViewPinClick}
            toggleFreeze={() => handleRestrictedAction(toggleFreeze)}
            isFrozen={isFrozen}
            isDisabledCard={isDisabledCard}
            remainingSeconds={remainingSeconds}
            isCountdownActive={isCountdownActive}
        />
    );
};

export default CarouselButtonsContainer;
